import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'

import RozmowaService from '@/services/RozmowaService'
import PlacowkaService from '@/services/PlacowkaService'
import OsobaService from '@/services/OsobaService'
import { toApiDate } from '../rozmowaDetailsHelpers'

const getRelationId = relation => {
	if (!relation) return ''
	if (typeof relation === 'object') return relation?._id || relation?.id || ''
	return relation
}

const getRelationName = relation => {
	if (!relation || typeof relation !== 'object') return ''
	return relation?.nazwa || relation?.imie_i_nazwisko || relation?.alternative_name || relation?.name || ''
}

const setPayloadValue = (payload, key, value) => {
	if (value === 0 || value === '0') {
		payload[key] = '0'
		return
	}

	if (value !== undefined && value !== null && value !== '') {
		payload[key] = value
	}
}

const buildRozmowaUpdatePayload = (rozmowa, fields) => {
	const placowka = rozmowa?.id_placowka || rozmowa?.placowka
	const osoba = rozmowa?.id_osoba || rozmowa?.osoba
	const payload = {
		feedback: `${fields.feedback || ''}`.slice(0, 5000),
		dig_nastepnej_rozmowy: toApiDate(fields.dig_nastepnej_rozmowy),
		szansa: !!fields.szansa,
	}

	setPayloadValue(payload, 'id_user', getRelationId(rozmowa?.id_user || rozmowa?.user))
	setPayloadValue(payload, 'id_placowka', getRelationId(placowka))
	setPayloadValue(payload, 'id_osoba', getRelationId(osoba))
	setPayloadValue(payload, 'imie_i_nazwisko', rozmowa?.imie_i_nazwisko || getRelationName(osoba))
	setPayloadValue(payload, 'dig_rozmowy', rozmowa?.dig_rozmowy)

	return payload
}

const hasPlacowkaLocationData = placowka => {
	if (!placowka || typeof placowka !== 'object') return false

	return !!(
		placowka?.id_miasto ||
		placowka?.id_gmina ||
		placowka?.id_powiat ||
		placowka?.id_wojewodztwo
	)
}

const hasOsobaDetails = osoba => {
	if (!osoba || typeof osoba !== 'object') return false

	return !!(
		osoba?.imie_i_nazwisko &&
		(osoba?.telefon || osoba?.email || osoba?.funkcja)
	)
}

export default function useRozmowaDetailsPage() {
	const { t } = useTranslation()
	const { id } = useParams()
	const navigate = useNavigate()
	const [rozmowa, setRozmowa] = useState(null)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)
	const [deleting, setDeleting] = useState(false)

	const handleBack = useCallback(() => {
		navigate('/komunikacja/rozmowy')
	}, [navigate])

	const handleEdit = useCallback(() => {
		if (!id) return
		navigate(`/komunikacja/rozmowy/edit/${id}`)
	}, [id, navigate])

	const handleDelete = useCallback(async () => {
		if (!id || deleting) return

		const confirm = await Swal.fire({
			title: t('notifications.confirmTitle'),
			text: t('conversations.details.deleteConfirm'),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#d33',
			confirmButtonText: t('notifications.confirmDeleteButton'),
			cancelButtonText: t('notifications.cancelButton'),
		})
		if (!confirm.isConfirmed) return

		setDeleting(true)
		const result = await RozmowaService.delete(id)

		if (!result.success) {
			await Swal.fire(
				t('conversations.messages.errorTitle'),
				result.error || t('conversations.details.messages.deleteFailed'),
				'error'
			)
			setDeleting(false)
			return
		}

		await Swal.fire(
			t('notifications.deletedTitle'),
			t('conversations.details.messages.deletedSuccess'),
			'success'
		)
		navigate('/komunikacja/rozmowy')
	}, [deleting, id, navigate, t])

	const handleSaveFields = useCallback(async fields => {
		if (!rozmowa) {
			throw new Error(t('conversations.messages.detailsLoadFailed'))
		}

		const payload = buildRozmowaUpdatePayload(rozmowa, fields)
		const result = await RozmowaService.update(id, payload)

		if (result?.error) {
			throw new Error(result.error)
		}

		const updatedData = result?.data && typeof result.data === 'object' ? result.data : {}

		setRozmowa(prev => ({
			...prev,
			...updatedData,
			id_placowka:
				updatedData?.id_placowka && typeof updatedData.id_placowka === 'object'
					? updatedData.id_placowka
					: prev?.id_placowka,
			id_osoba:
				updatedData?.id_osoba && typeof updatedData.id_osoba === 'object'
					? updatedData.id_osoba
					: prev?.id_osoba,
			id_user:
				updatedData?.id_user && typeof updatedData.id_user === 'object'
					? updatedData.id_user
					: prev?.id_user,
			feedback: updatedData?.feedback ?? fields.feedback,
			dig_nastepnej_rozmowy: updatedData?.dig_nastepnej_rozmowy ?? toApiDate(fields.dig_nastepnej_rozmowy),
			szansa: updatedData?.szansa ?? !!fields.szansa,
		}))
	}, [id, rozmowa, t])

	useEffect(() => {
		let active = true

		const loadDetails = async () => {
			if (!id) {
				setRozmowa(null)
				setLoading(false)
				return
			}

			setLoading(true)
			setError(null)

			try {
				const result = await RozmowaService.getById(id)

				if (!active) return

				if (result?.error) {
					throw new Error(result.error)
				}

				const rozmowaData = result?.data || null
				const placowkaValue = rozmowaData?.id_placowka || rozmowaData?.placowka
				const placowkaId = getRelationId(placowkaValue)
				let placowkaData = placowkaValue && typeof placowkaValue === 'object' ? placowkaValue : null

				const osobaValue = rozmowaData?.id_osoba || rozmowaData?.osoba
				const osobaId = getRelationId(osobaValue)
				let osobaData = osobaValue && typeof osobaValue === 'object' ? osobaValue : null

				const enrichmentFetches = []

				if (placowkaId && !hasPlacowkaLocationData(placowkaData)) {
					enrichmentFetches.push(
						PlacowkaService.getById(placowkaId).then(placowkaResult => ({
							type: 'placowka',
							result: placowkaResult,
						}))
					)
				}

				if (osobaId && !hasOsobaDetails(osobaData)) {
					enrichmentFetches.push(
						OsobaService.getById(osobaId).then(osobaResult => ({
							type: 'osoba',
							result: osobaResult,
						}))
					)
				}

				if (enrichmentFetches.length > 0) {
					const enrichmentResults = await Promise.all(enrichmentFetches)

					if (!active) return

					for (const { type, result: enrichmentResult } of enrichmentResults) {
						if (enrichmentResult?.error) continue

						if (type === 'placowka') {
							placowkaData = enrichmentResult?.data || placowkaData
						}

						if (type === 'osoba') {
							osobaData = enrichmentResult?.data || osobaData
						}
					}
				}

				setRozmowa({
					...rozmowaData,
					id_placowka: placowkaData || placowkaValue,
					id_osoba: osobaData || osobaValue,
				})
			} catch (err) {
				if (!active) return

				setError(err.message || t('conversations.messages.detailsLoadFailed'))
				setRozmowa(null)
			} finally {
				if (active) setLoading(false)
			}
		}

		loadDetails()

		return () => {
			active = false
		}
	}, [id, t])

	return {
		deleting,
		error,
		handleBack,
		handleDelete,
		handleEdit,
		handleSaveFields,
		loading,
		rozmowa,
	}
}
